import { APP_NAME } from '@/branding';
import { useState } from 'react';
import { Link } from 'react-router';
import { CheckCheck, Loader2 } from 'lucide-react';
import {
  RULE_CATEGORIES,
  useInboxPage,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  type AppNotification,
} from '@/hooks/useNotifications.ts';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { RelativeTime } from '@/components/RelativeTime.tsx';
import { SeverityBadge } from '@/components/SeverityBadge.tsx';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * The inbox, at last readable.
 *
 * The bell has always shown the newest thirty with no filter and no way back — which
 * is a strange thing to do to the one surface the design deliberately exempts from
 * every anti-spam layer (§7.9). In-app is the *complete* record, kept complete so
 * "why didn't I hear about that?" is answerable; a reader that can only see the last
 * page cannot ask that question.
 *
 * **This is not the delivery log.** These are the rows that reached you in-app. The
 * delivery log is every decision the router made about you on every channel, the
 * skipped ones included, and it is where a missing email is explained. The "Delivery
 * details" link bridges them per row.
 */

const PAGE_SIZE = 25;
const ALL = '__all__';

function InboxRow({ notification, onRead }: { notification: AppNotification; onRead: (n: AppNotification) => void }) {
  const category = RULE_CATEGORIES.find((c) => c.key === notification.category)?.label ?? notification.category;

  return (
    <li
      className={cn(
        'rounded-md border px-3 py-2.5 space-y-1',
        notification.read ? 'border-border-subtle' : 'border-border bg-muted/30',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Absent on rows written before severity was carried, rather than
                defaulted to something that would read as a real classification. */}
            {notification.severity ? <SeverityBadge severity={notification.severity} className="text-[10px]" /> : null}
            <span className="text-xs text-muted-foreground">{category}</span>
            {notification.read ? null : (
              <span className="text-[10px] uppercase tracking-wide text-sev-5 font-semibold">Unread</span>
            )}
          </div>

          <p className="text-sm break-words">
            {notification.link ? (
              <Link to={notification.link} className="hover:underline" onClick={() => { onRead(notification); }}>
                {notification.title}
              </Link>
            ) : (
              notification.title
            )}
          </p>

          {notification.body ? (
            <p className="text-xs text-muted-foreground break-words">{notification.body}</p>
          ) : null}
        </div>

        <span className="flex items-center gap-2 shrink-0">
          <RelativeTime value={notification.createdAt} className="text-xs text-muted-foreground" />
        </span>
      </div>

      <div className="flex items-center gap-3">
        {/* The bridge to the log. Only where the row came from a routing event —
            older inbox rows predate the link and have nothing to point at.

            Not "why this one?", which is a different question with a different
            answer: why a rule did or did not match is the explain drawer on the
            incident, and it re-runs the matcher. This is the record of what the
            router did once it had decided. */}
        {notification.eventId ? (
          <Link
            to={`/notifications/deliveries?eventId=${notification.eventId}`}
            title="Every channel this notification was routed to, and what happened on each"
            className="text-xs text-muted-foreground hover:text-foreground underline decoration-dotted underline-offset-2"
          >
            Delivery details
          </Link>
        ) : null}
        {notification.read ? null : (
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground underline decoration-dotted underline-offset-2"
            onClick={() => { onRead(notification); }}
          >
            Mark read
          </button>
        )}
      </div>
    </li>
  );
}

export default function Inbox() {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [category, setCategory] = useState<string>(ALL);
  // "Load more" rather than numbered pages: the inbox is a reverse-chronological feed
  // whose newest end shifts as things happen, so a page number names a different set
  // of rows minute to minute.
  const [pages, setPages] = useState(1);

  const { data, isLoading, isFetching } = useInboxPage({
    unreadOnly,
    category: category === ALL ? null : category,
    limit: PAGE_SIZE * pages,
  });

  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = data?.notifications ?? [];
  const unread = data?.unreadCount ?? 0;
  // The page asked for `PAGE_SIZE * pages` and got fewer, so there is no more to get.
  const exhausted = notifications.length < PAGE_SIZE * pages;

  // Marks read and nothing else. The title is a <Link>, which pushes its own history
  // entry — an imperative navigate() here pushed a second one for the same click, so
  // Back needed two presses to leave the incident, and it made "Mark read" a one-way
  // trip into the details of the thing you were trying to dismiss without opening.
  const onRead = (n: AppNotification) => {
    if (!n.read) markRead.mutate(n.id);
  };

  return (
    <PageContainer width="medium">
      <PageHeader
        title="Inbox"
        subtitle={`Everything ${APP_NAME} has told you in the app`}
        primary={
          unread > 0 ? (
            <Button variant="outline" size="sm" onClick={() => { markAllRead.mutate(); }} disabled={markAllRead.isPending}>
              <CheckCheck className="h-3.5 w-3.5 mr-1.5" />
              Mark all read
            </Button>
          ) : undefined
        }
      />

      <section className="bg-card border border-border rounded-lg p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs">
            <Switch
              checked={unreadOnly}
              onCheckedChange={(v) => { setUnreadOnly(v); setPages(1); }}
              aria-label="Show unread only"
            />
            Unread only
          </label>

          <div className="flex items-center gap-2">
            <Label className="text-xs">Category</Label>
            <Select
              value={category}
              onValueChange={(v) => { setCategory(v); setPages(1); }}
            >
              <SelectTrigger className="h-8 text-xs w-44" aria-label="Category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Everything</SelectItem>
                {RULE_CATEGORIES.map((c) => (
                  <SelectItem key={c.key} value={c.key}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <span className="text-xs text-muted-foreground ml-auto">
            {unread} unread
          </span>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : notifications.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {unreadOnly || category !== ALL
              ? 'Nothing matches those filters.'
              : 'Nothing yet. Notifications appear here once something happens that one of your rules matches.'}
          </p>
        ) : (
          <>
            <ul className="space-y-2">
              {notifications.map((n) => (
                <InboxRow key={n.id} notification={n} onRead={onRead} />
              ))}
            </ul>

            {exhausted ? null : (
              <Button
                variant="outline"
                size="sm"
                disabled={isFetching}
                onClick={() => { setPages((p) => p + 1); }}
              >
                {isFetching ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                Load more
              </Button>
            )}
          </>
        )}
      </section>
    </PageContainer>
  );
}
